import { cur } from '@/lib/currency';
import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { DateFilter } from '@/components/DateFilter';
import { ExportButtons } from '@/components/ExportButtons';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ClipboardList, Search } from 'lucide-react';
import { ReconciledBadge } from '@/components/accounting/ReconciledBadge';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AccountSummaryPage() {
  const [data, setData] = useState(null);
  const [dateFilter, setDateFilter] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showZero, setShowZero] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
      const res = await api.get('/accounting/account-summary', { params });
      setData(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [dateFilter]);

  // iter432 — Search + zero-balance toggle for Account Summary.
  const filteredAccounts = useMemo(() => {
    const all = data?.accounts || [];
    const q = search.trim().toLowerCase();
    return all.filter(a => {
      if (!showZero && !(a.transaction_count > 0 || a.balance !== 0)) return false;
      if (!q) return true;
      return (a.code || '').toLowerCase().includes(q)
        || (a.name || '').toLowerCase().includes(q)
        || (a.type || '').toLowerCase().includes(q);
    });
  }, [data, search, showZero]);

  const typeColors = { asset: 'bg-info/20 text-info', liability: 'bg-error/20 text-error', equity: 'bg-purple-100 text-purple-700', revenue: 'bg-success/20 text-success', expense: 'bg-orange-100 text-orange-700' };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="account-summary-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200/50">
              <ClipboardList size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground flex items-center gap-3">Account Summary{data && <ReconciledBadge debit={data.total_debit} credit={data.total_credit} difference={data.difference} />}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Overview of all accounts with balances</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DateFilter onFilterChange={setDateFilter} />
            <ExportButtons dataType="journal_entries" label="Account Summary" startDate={dateFilter.start} endDate={dateFilter.end} />
          </div>
        </div>

        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total Accounts</p><p className="text-2xl font-bold">{data.total_accounts}</p></CardContent></Card>
            <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Grand Total Debits <span className="normal-case text-primary/70">· ties to Trial Balance</span></p><p className="text-2xl font-bold text-success">{cur()} {data.total_debit?.toLocaleString()}</p></CardContent></Card>
            <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Grand Total Credits <span className="normal-case text-primary/70">· ties to Trial Balance</span></p><p className="text-2xl font-bold text-error">{cur()} {data.total_credit?.toLocaleString()}</p></CardContent></Card>
          </div>
        )}

        <Card>
          <CardContent className="p-0">
            <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 flex-wrap" data-testid="as-toolbar">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search code, name or type…" className="h-8 pl-7 text-xs" data-testid="as-search" />
              </div>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground select-none cursor-pointer">
                <input type="checkbox" checked={showZero} onChange={e => setShowZero(e.target.checked)} data-testid="as-show-zero" />
                Show zero-balance / no-activity
              </label>
              <span className="text-[10px] text-muted-foreground">{filteredAccounts.length} of {data?.accounts?.length || 0}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b"><tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Code</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Account Name</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Total Debits</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Total Credits</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Net Balance</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Txns</th>
                </tr></thead>
                <tbody>
                  {loading ? <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</td></tr> :
                    filteredAccounts.map((a, i) => (
                    <tr key={i} className="border-b hover:bg-muted/50">
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{a.code}</td>
                      <td className="px-4 py-3 font-medium">{a.name}</td>
                      <td className="px-4 py-3"><Badge className={`text-[10px] capitalize ${typeColors[a.type] || 'bg-muted text-foreground'}`}>{a.type}</Badge></td>
                      <td className="px-4 py-3 text-right text-success" data-testid={`as-gross-debit-${a.code}`}>{a.gross_debit > 0 ? `${cur()} ${a.gross_debit.toLocaleString()}` : '-'}</td>
                      <td className="px-4 py-3 text-right text-error" data-testid={`as-gross-credit-${a.code}`}>{a.gross_credit > 0 ? `${cur()} ${a.gross_credit.toLocaleString()}` : '-'}</td>
                      <td className={`px-4 py-3 text-right font-bold ${a.balance >= 0 ? 'text-foreground' : 'text-error'}`} data-testid={`as-net-balance-${a.code}`}>{cur()} {a.balance?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{a.transaction_count}</td>
                    </tr>
                  ))}
                  {!loading && filteredAccounts.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">{search ? 'No accounts match your search' : 'No account activity'}</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
